import { cn } from "@fatbees/ui-utils";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import {
  FocusEventHandler,
  ForwardedRef,
  forwardRef,
  useId,
  useRef,
} from "react";
import { useDateFormatString, useFormatDate, useParseDate } from "../i18n";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Input, InputProps, inputVariants } from "./input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "./popover";

export interface DateInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "onBlur" | "ref"
> {
  value?: Date | null;
  showTime?: boolean;
  onChange?: (event: {
    target: { value: Date | null };
    type: "change";
  }) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const dateFormat: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};
const dateTimeFormat: Intl.DateTimeFormatOptions = {
  ...dateFormat,
  hour: "numeric",
  hour12: true,
  minute: "2-digit",
};

export const DateInput = forwardRef(function DateInput(
  {
    value = null,
    showTime = false,
    onChange,
    placeholder,
    disabled = false,
    className,
    ...props
  }: DateInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const options = showTime ? dateTimeFormat : dateFormat;
  const parseDate = useParseDate();
  const formatDate = useFormatDate(options);
  const dateFormatString = useDateFormatString(options);
  const divRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const calendarId = useId();
  const [date, setDate] = React.useState<Date | null>(value);
  const [inputValue, setInputValue] = React.useState<string>("");
  const [open, setOpen] = React.useState(false);
  const [isValidState, setIsValid] = React.useState(true);
  const [month, setMonth] = React.useState<Date | null>(date);

  const localePlaceholder = React.useMemo(
    () => placeholder || dateFormatString.toLowerCase(),
    [placeholder, dateFormatString],
  );

  // Update internal state when prop changes
  React.useEffect(() => {
    if (value) {
      setDate(value);
      setInputValue(formatDate(value) as string);
    } else {
      setDate(null);
      setInputValue("");
    }
  }, [value, formatDate]);

  // Handle manual input - don't validate during typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsValid(true); // Reset validation state while typing
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsedDate = parseDate(e.target.value);

    if (parsedDate) {
      // Update the calendar
      setDate(parsedDate);
      if (String(parsedDate.getFullYear()).length === 4) {
        // Avoid bouncing the calendar around when they're typing the year
        setMonth(parsedDate);
      }
    }
  };

  // Handle input focus - open calendar automatically
  const handleInputFocus = () => {
    // Give the other code a chance to process this focus event before opening
    // the calendar
    setTimeout(() => {
      setOpen(true);
    });
  };

  // Handle calendar selection
  const handleCalendarSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      if (date && showTime) {
        selectedDate.setHours(date.getHours());
        selectedDate.setMinutes(date.getMinutes());
        selectedDate.setSeconds(date.getSeconds());
        selectedDate.setMilliseconds(date.getMilliseconds());
      }

      setDate(selectedDate);
      setInputValue(formatDate(selectedDate) as string);
      onChange?.({ target: { value: selectedDate }, type: "change" });
      setIsValid(true);
    } else {
      setDate(null);
      setInputValue("");
      onChange?.({ target: { value: null }, type: "change" });
    }
    setOpen(false);
  };

  // Validate and parse date when user finishes typing
  const handleBlur: FocusEventHandler = (e) => {
    if (popoverRef.current?.contains(e.relatedTarget)) {
      // Ignore blur events that come from the user clicking on the calendar
      return;
    }

    // Try to parse with the detected locale format
    const parsedDate = parseDate(inputValue);

    if (parsedDate) {
      setDate(parsedDate);
      onChange?.({ target: { value: parsedDate }, type: "change" });
      setIsValid(true);
      setInputValue(formatDate(parsedDate) as string);
    } else {
      setIsValid(false);
      onChange?.({ target: { value: null }, type: "change" });
      // Don't clear the input, let user see their invalid input
    }
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          className={cn("relative flex items-center", className)}
          ref={divRef}
        >
          <Input
            {...props}
            type="text"
            inputMode="numeric"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            placeholder={localePlaceholder}
            disabled={disabled}
            className={cn(
              inputVariants(props),
              "flex-1",
              "pr-10",
              !isValidState && "border-red-500 focus-visible:ring-red-500",
            )}
            aria-label="Date input"
            aria-invalid={!isValidState}
            ref={ref}
          />
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size={props.size}
              className="absolute right-0 rounded-l-none border-l"
              disabled={disabled}
              aria-label="Open calendar"
              role="presentation"
              tabIndex={-1}
              onClick={(e) => {
                if (open) {
                  // Clicking on the button when the calendar is open should not
                  // close it
                  e.preventDefault();
                }
              }}
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        ref={popoverRef}
        className="w-auto p-0"
        align="start"
        onOpenAutoFocus={(e) => {
          // Keep the focus on the input when opening
          e.preventDefault();
        }}
      >
        <Calendar
          mode="single"
          selected={date ?? undefined}
          onSelect={handleCalendarSelect}
          month={month ?? undefined}
          onMonthChange={setMonth}
          id={calendarId}
        />
      </PopoverContent>
    </Popover>
  );
});
